import { Redirect, router, useLocalSearchParams } from 'expo-router';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ActionSheet, Text, useMqSelect } from '@fhs/ui';
import ConfirmDialog from '@fhs-legacy/frontend/src/components/confirm-dialog';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { SimplyOfferClickEventLabels, SimplyOfferEventNames } from '../../analytics';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { ReplaceIncentiveModalProvider } from '../../modals/replace-incentive-modal/replace-incentive-modal';
import { useOfferDetailUi } from '../../queries/loyalty.queries';
import {
  useIsLoadingTierSelector,
  useIsOfferCartDirty,
  useOfferCart,
  useSelectedStep,
  useShouldRenderTierSelector,
} from '../../state/offer-guide-cart-state';

import { TIER_SELECTOR_HEIGHT } from './constants';
import { ContentRenderer } from './content-renderer';
import { Navigation } from './navigation';

const initialExitDialog = {
  onDismiss: () => {},
  onConfirm: () => {},
  showDialog: false,
};

export function OfferGuideScreen() {
  const { offerId } = useLocalSearchParams<{ offerId: string }>();
  const setOffer = useOfferCart(state => state.setOffer);
  const reset = useOfferCart(state => state.reset);
  const { data: offer } = useOfferDetailUi(offerId);
  const { isAuthenticated } = useLoyaltyLegacyStates();
  const enableSimplifiedMenuDetailView = useFlag(
    LaunchDarklyFlag.ENABLE_SIMPLIFIED_MENU_INSIDE_GUIDE_OFFERS
  ) as boolean;

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (offer) {
      setOffer(cloneDeep(offer), enableSimplifiedMenuDetailView);
    }
  }, [setOffer, offer, enableSimplifiedMenuDetailView]);

  if (!isAuthenticated) {
    return <Redirect href={routes.signIn} />;
  }

  if (offer && !offer.requireGuideFlow) {
    return <Redirect href={routes.offersV2Details + `/${offer.id}`} />;
  }

  return <OfferGuideContent />;
}

export function OfferGuideContent() {
  const { offerId } = useLocalSearchParams<{
    offerId: string;
  }>();
  const isLoadingTierSelector = useIsLoadingTierSelector();
  const { logRBIEvent } = useCRMEventsContext();
  const { height: screenHeight } = useWindowDimensions();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const [confirmExitDialog, setConfirmExitDialog] = useState(initialExitDialog);
  const drawerProps = useMemo(() => ({ style: { flex: 1 } }), []);

  const {
    currentStepIndex,
    goToPrevStep,
    goToStep,
    selectedEligibleItem,
    setSelectedEligibleItem,
    onBackAttempt,
    offerInfo,
    setSelectedTierId,
  } = useOfferCart();
  const shouldRenderTierSelector = useShouldRenderTierSelector();
  const selectedStep = useSelectedStep();

  const hasSingleEligibleItem = selectedStep?.eligibleItems?.length === 1;

  const avoidClickEvents = selectedEligibleItem && !selectedEligibleItem.requiresCustomization;

  const overrideHeight =
    shouldRenderTierSelector || isLoadingTierSelector ? TIER_SELECTOR_HEIGHT : undefined;

  const isDirty = useIsOfferCartDirty();

  const close = useCallback(() => {
    router.dismiss();
  }, []);

  const onClose = useCallback(() => {
    if (isDirty) {
      logRBIEvent({
        name: SimplyOfferEventNames.OFFER_GUIDE_EXIT_DIALOG_SHOWN,
        type: EventTypes.Other,
        attributes: { offerId, sanityId: offerInfo?.cmsId },
      });
      setConfirmExitDialog({
        showDialog: true,
        onConfirm: () => {
          setConfirmExitDialog(initialExitDialog);
          logRBIEvent({
            name: CustomEventNames.CLICK_EVENT,
            type: EventTypes.Other,
            attributes: {
              component: ClickEventComponentNames.BUTTON,
              text: SimplyOfferClickEventLabels.OFFER_GUIDE_LEAVE_OFFER,
            },
          });
          close();
        },
        onDismiss: () => {
          setConfirmExitDialog(initialExitDialog);
          logRBIEvent({
            name: CustomEventNames.CLICK_EVENT,
            type: EventTypes.Other,
            attributes: {
              component: ClickEventComponentNames.BUTTON,
              text: SimplyOfferClickEventLabels.OFFER_GUIDE_STAY_ON_OFFER,
            },
          });
        },
      });
      return;
    }
    close();
    return;
  }, [isDirty, close, logRBIEvent, offerId, offerInfo?.cmsId]);

  const onPressPrev = useCallback(() => {
    if (avoidClickEvents) {
      return;
    }
    if (onBackAttempt) {
      const preventDefault = onBackAttempt();
      if (preventDefault) {
        return;
      }
    }
    if (selectedEligibleItem && !hasSingleEligibleItem) {
      setSelectedEligibleItem(undefined);
      return;
    }
    if (currentStepIndex === 0) {
      if (offerInfo?.tiers?.length) {
        setSelectedTierId(undefined);
        return;
      }
      onClose();
      return;
    }
    goToPrevStep();
    logRBIEvent({
      name: SimplyOfferEventNames.OFFER_GUIDE_WENT_PREV_STEP,
      type: EventTypes.Other,
      attributes: { offerId, sanityId: offerInfo?.cmsId },
    });
  }, [
    goToPrevStep,
    onClose,
    selectedEligibleItem,
    currentStepIndex,
    setSelectedEligibleItem,
    onBackAttempt,
    logRBIEvent,
    offerId,
    offerInfo?.cmsId,
    setSelectedTierId,
    offerInfo?.tiers?.length,
    hasSingleEligibleItem,
    avoidClickEvents,
  ]);

  const onPressStep = useCallback(
    (index: number) => {
      if (avoidClickEvents) {
        return;
      }
      goToStep({ index });
      logRBIEvent({
        name: SimplyOfferEventNames.OFFER_GUIDE_STEP_SELECTED,
        type: EventTypes.Other,
        attributes: { offerId, sanityId: offerInfo?.cmsId, stepIndex: index },
      });
    },
    [goToStep, logRBIEvent, offerId, offerInfo?.cmsId, avoidClickEvents]
  );

  const containerStyle = isDesktop
    ? {
        height: overrideHeight ? undefined : screenHeight,
        maxHeight: overrideHeight ?? screenHeight,
      }
    : {};

  return (
    <ActionSheet
      onRequestClose={onPressPrev}
      isVisible
      onClose={close}
      containerStyle={[
        styles.actionSheet,
        overrideHeight && {
          height: null,
          maxHeight: overrideHeight,
        },
      ]}
      ignoreBottomSafeArea
      drawerProps={drawerProps}
    >
      <ReplaceIncentiveModalProvider>
        <SafeAreaView
          edges={Platform.OS === 'android' ? ['bottom'] : []}
          style={[styles.container, containerStyle]}
        >
          <Navigation onPressPrev={onPressPrev} onPressStep={onPressStep} onClose={onClose} />
          <ContentRenderer />
        </SafeAreaView>
      </ReplaceIncentiveModalProvider>
      <ConfirmDialog
        modalAppearanceEventMessage="Confirm Close Offer Selection"
        showDialog={confirmExitDialog.showDialog}
        onConfirm={confirmExitDialog.onDismiss}
        onDismiss={confirmExitDialog.onDismiss}
        onCancel={confirmExitDialog.onConfirm}
        confirmLabel="Stay Here"
        cancelLabel="Leave"
        bodyComponent={
          <Text style={{ marginBottom: 20 }}>
            The offer won't be applied and you are going to lose all your choices and
            customizations.
          </Text>
        }
        heading="Are You Sure You Want to Leave?"
      />
    </ActionSheet>
  );
}

const styles = StyleSheet.create({
  container: {
    height: '100%',
  },
  actionSheet: {
    height: '90%',
  },
});
